<template>
  <div class="sp-stepper-actions" part="base" :class="classModifiers">
    <slot name="prev" @click="emit('prev')">
      <sp-button
        part="prev-action"
        :block="isBlockType"
        :disabled="isPrevDisabled"
        :variant="prevActionVariant"
        :loading="prevActionLoading"
        :color="prevActionColor"
        tabindex="0"
      >
        <sp-icon v-if="showChevronLeftIcon" slot="prepend" name="chevron-left-filled"></sp-icon>
        {{ prevActionText }}
      </sp-button>
    </slot>

    <slot name="next" @click="emit('next')">
      <sp-button
        part="next-action"
        :data-testid="nextTestId"
        :block="isBlockType"
        :disabled="isNextDisabled"
        :variant="nextActionVariant"
        :loading="nextActionLoading"
        :color="nextActionColor"
        tabindex="0"
      >
        {{ nextActionText }}
      </sp-button>
    </slot>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { booleanAttribute, toBoolean } from "#ce/utils/props";

const emit = defineEmits(["prev", "next"]);

const props = defineProps({
  prevActionText: {
    type: String,
    default: "Prev",
  },
  prevActionColor: {
    type: String,
    default: undefined,
  },
  prevActionVariant: {
    type: String,
    default: undefined,
  },
  prevActionLoading: booleanAttribute(),
  nextActionText: {
    type: String,
    default: "Next",
  },
  nextActionColor: {
    type: String,
    default: undefined,
  },
  nextActionVariant: {
    type: String,
    default: undefined,
  },
  nextActionLoading: booleanAttribute(),
  /**
   * Sets a `data-testid` on the default next-action button.
   *
   * @type {String}
   * @default undefined
   */
  nextTestId: {
    type: String,
    default: undefined,
  },
  /**
   * Whether the actions are disabled
   *
   * @type {Boolean|String} - true, false, "prev", "next"
   * @default false
   */
  disabled: booleanAttribute(),
});

const { isSmScreen, breakpointModifiers, gtModifiers } = useBreakpoints();

const isDisabled = computed(() => toBoolean(props.disabled));
const isPrevDisabled = computed(() => props.disabled === "prev" || isDisabled.value);
const isNextDisabled = computed(() => props.disabled === "next" || isDisabled.value);
const isPrevActionVariantOutlined = computed(() => props.prevActionVariant === "outlined");
const showChevronLeftIcon = computed(() => isSmScreen.value && !isPrevActionVariantOutlined.value);

const prevActionLoading = ref(toBoolean(props.prevActionLoading));
watch(
  () => props.prevActionLoading,
  () => (prevActionLoading.value = toBoolean(props.prevActionLoading)),
);

const nextActionLoading = ref(toBoolean(props.nextActionLoading));
watch(
  () => props.nextActionLoading,
  () => (nextActionLoading.value = toBoolean(props.nextActionLoading)),
);

const isBlockType = computed(() => isSmScreen.value);

const classModifiers = computed(() => [...breakpointModifiers.value, ...gtModifiers.value]);

const prevActionVariant = computed(() => {
  if (props.prevActionVariant) {
    return props.prevActionVariant;
  }
  return isSmScreen.value ? "text-inline" : "outlined";
});

const prevActionColor = computed(() => props.prevActionColor ?? "primary");

const nextActionVariant = computed(() => props.nextActionVariant ?? "flat");
const nextActionColor = computed(() => props.nextActionColor ?? "primary");
</script>

<style>
:host {
  display: block;
  width: 100%;
}
</style>

<style scoped lang="scss">
.sp-stepper-actions {
  --flex-direction: column;
  --gap: var(--sp-comp-stepper-actions-gap, 0);
  display: flex;
  flex-direction: var(--flex-direction);
  justify-content: space-between;
  position: relative;
  gap: var(--gap);
  border-top: var(--sp-comp-stepper-actions-border-top, none);
  padding-block: var(--sp-ref-scale-16);
  padding-inline: var(--sp-ref-scale-16);
  box-shadow: var(--sp-comp-stepper-actions-box-shadow, none);

  &.--gt-sm-screen {
    --flex-direction: row;
    --gap: var(--sp-comp-stepper-actions-gap-gt-sm, 1rem);
  }

  sp-button::part(prev-action) {
    text-align: center;
    --button-width: 100%;
  }
}
</style>
